import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useCurrentAdvisor } from './useCurrentAdvisor'

export function useNotifications() {
  const { advisor } = useCurrentAdvisor()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchNotifications = useCallback(async (advisorId) => {
    const { data, error } = await supabase
      .from('sfp_notifications')
      .select('*')
      .eq('advisor_id', advisorId)
      .order('created_at', { ascending: false })
      .limit(50)

    if (!error) {
      setNotifications(data ?? [])
      setUnreadCount((data ?? []).filter((n) => !n.is_read).length)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!advisor?.id) return

    fetchNotifications(advisor.id)

    const channel = supabase
      .channel(`sfp-notifications-${advisor.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sfp_notifications',
          filter: `advisor_id=eq.${advisor.id}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new, ...prev])
          setUnreadCount((prev) => prev + 1)
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIPTION_ERROR') {
          console.debug('[useNotifications] Realtime unavailable (non-critical)')
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [advisor?.id, fetchNotifications])

  const markAsRead = useCallback(async (notificationId) => {
    const { error } = await supabase
      .from('sfp_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', notificationId)

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    if (!advisor?.id) return
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id)
    if (unreadIds.length === 0) return

    const { error } = await supabase
      .from('sfp_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .in('id', unreadIds)

    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
      setUnreadCount(0)
    }
  }, [advisor?.id, notifications])

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead }
}
